import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import _ from "lodash"
import type {
    AccessData,
    Customer,
    CustomerAddress,
    CustomerAddressDeliveryType,
    CustomerLoyaltyProgram,
    CustomerNote,
    GenderType,
    InventoryItem,
    LoyaltyProgramData,
    UserAccount,
} from "./types"
import type { ObjectOfVersions } from "../operations/reducer"
import type {
    CustomerAddressResponseItem,
    CustomerNoteResponseItem,
    CustomerResponseItem,
    GetCustomerServerResponse,
} from "../../api/customerDetail"
import type {
    GetUserAccountServerResponse,
    UserAccountAddressServerItem,
    UserAccountLocationServerItem,
    UserAccountNoteServerItem,
    UserAccountResponseItem,
    UserAccountRoleServerItem,
} from "../../api/userAccount"
import { AccessLevelDTO, PermissionDTO } from "../../api/accessAndRoles"
import type { LoyaltyProgramResponseServerItem } from "../../api/loyaltyProgram"
import type {
    GetInventoryServerResponse,
    InventoryResponseItem,
    InventoryResponseItemLocation,
} from "../../api/inventory"
import type { LocationAuthData } from "../auth/types"

const resolveGender = (gender: Maybe<string>): GenderType => {
    if (!gender) {
        return null
    }

    if (gender.toLowerCase() === "male") {
        return "male"
    }

    if (gender.toLowerCase() === "female") {
        return "female"
    }

    return null
}

const resolveDelivery = (delivery: Maybe<string>): Maybe<CustomerAddressDeliveryType> => {
    switch (delivery) {
        case "delivery":
            return "delivery"

        case "billing":
            return "billing"

        case "office":
            return "office"

        case "home":
            return "home"

        case "off-site":
            return "off-site"

        case "other":
            return "other"

        default:
            return null
    }
}

export const transformLoyaltyProgramData = (content: LoyaltyProgramResponseServerItem[]): LoyaltyProgramData[] =>
    content.map((data: LoyaltyProgramResponseServerItem) => ({
        id: data.id,
        rewardsIncentive: data.rewards_incentive,
        redemptionMinimum: data.redemption_minimum,
        startDate: data.start_date,
    }))

export const transformCustomerNotes = (serverNotes: CustomerNoteResponseItem[]): CustomerNote[] =>
    _.sortBy(
        serverNotes
            .filter(n => !n.deleted)
            .map((data: CustomerNoteResponseItem) => ({
                id: data.id,
                customerId: data.customer_id,
                note: data.note,
                timestamp: data.origin_timestamp,
                userName: data.origin_user_name,
            })),
        "timestamp",
        "desc"
    )

export const transformCustomerAddresses = (addresses: CustomerAddressResponseItem[]): CustomerAddress[] =>
    addresses.map((data: CustomerAddressResponseItem) => ({
        id: data.id,
        city: data.city,
        customerId: data.customer_id,
        line1: data.line1,
        line2: data.line2,
        state: data.state,
        type: resolveDelivery(data.type),
        version: data.version,
        zip: data.zip,
    }))

export const filterLoyaltyPrograms = (loyaltyPrograms: CustomerLoyaltyProgram[], loyaltyProgram: any): Maybe<any> => {
    if (!loyaltyProgram) {
        return null
    }

    return loyaltyPrograms.filter(lp => lp.deleted === false).find(clp => clp.loyalty_program_id === loyaltyProgram.id)
}

export const transformCustomerServerData = (
    serverData: GetCustomerServerResponse,
    loyaltyProgram: Maybe<LoyaltyProgramData>
): Customer[] =>
    serverData.content.map((data: CustomerResponseItem) => ({
        id: data.id,
        companyName: data.company_name || null,
        firstName: data.first_name || null,
        surname: data.surname || null,
        isCompany: data.is_company,
        position: data.position,
        addresses: data.addresses && transformCustomerAddresses(data.addresses),
        // TODO here
        dateOfBirth: data.date_of_birth,
        email: data.email_address,
        gender: resolveGender(data.gender),
        paymentCardIds: data.payment_card_ids,
        phoneNumber: data.phone_number,
        customerLoyaltyProgram:
            data.customer_loyalty_programs && data.customer_loyalty_programs.length > 0
                ? filterLoyaltyPrograms(data.customer_loyalty_programs, loyaltyProgram)
                : null,
        title: data.title,
        notes: data.notes && transformCustomerNotes(data.notes),
    }))

export const transformInventoryItemServerData = (
    serverData: GetInventoryServerResponse,
    locations: LocationAuthData[]
): InventoryItem[] =>
    serverData.content
        .filter(iri => !iri.deleted)
        .map((data: InventoryResponseItem) => ({
            id: data.id,
            categoryId: data.category_id,
            isRestockableOnVoid: data.is_restockable_on_void,
            isRestockableOnRefund: data.is_restockable_on_refund,
            lowThreshold: data.low_threshold || null,
            inventoryItemLocations: data.inventory_item_locations
                .filter(il => !il.deleted && !!locations.find(l => l.id === il.location_id))
                .map((il: InventoryResponseItemLocation) => ({
                    id: il.id,
                    inventoryItemId: il.inventory_item_id,
                    locationId: il.location_id,
                    quantityTotal: il.quantity_total,
                })),
        }))

// User Accounts (Employees)

export const transformUserAccountsServerData = (serverData: GetUserAccountServerResponse): UserAccount[] =>
    serverData.content.map((data: UserAccountResponseItem) => ({
        id: data.id,
        accountId: data.account_id,
        deleted: data.deleted,
        email: data.email,
        firstName: data.first_name || "",
        middleName: data.middle_name || "",
        isActive: data.is_active,
        locationIds: data.location_ids,
        locations: data.locations
            ? data.locations.map((l: UserAccountLocationServerItem) => ({
                  id: l.id,
                  locationId: l.location_id,
                  userAccountId: l.user_account_id,
              }))
            : [],
        phone: data.phone,
        pin: data.pin,
        accessCardKey: data.access_card_key || null,
        roles: data.roles
            ? data.roles.map((r: UserAccountRoleServerItem) => ({
                  id: r.id,
                  roleId: r.role_id,
                  userAccountId: r.user_account_id,
                  salaryRate: r.salary_rate,
                  rateType: r.rate_type,
              }))
            : [],
        surname: data.surname,
        userId: data.user_id,
        accessId: data.access_level_id,
        gender: data.gender,
        ssn: data.ssn,
        employeeNumber: data.employee_number,
        dateOfBirth: data.date_of_birth,
        addresses: data.addresses
            ? data.addresses.map((a: UserAccountAddressServerItem) => ({
                  id: a.id,
                  deleted: a.deleted,
                  line1: a.line1,
                  line2: a.line2,
                  state: a.state,
                  city: a.city,
                  zip: a.zip,
                  country: a.country,
              }))
            : [],
        inactiveReason: data.inactive_reason,
        emergencyContactName: data.emergency_contact_name,
        emergencyContactMeans: data.emergency_contact_means,
        notes: data.notes
            ? data.notes
                  .sort((a, b) => a.origin_timestamp.localeCompare(b.origin_timestamp))
                  .map((n: UserAccountNoteServerItem) => ({
                      id: n.id,
                      note: n.note,
                      deleted: n.deleted,
                      originUserAccountId: n.origin_user_account_id,
                      originTimestamp: n.origin_timestamp,
                      userAccountId: n.user_account_id,
                      originUserName: n.origin_user_name,
                  }))
            : [],
    }))
// Access and Roles

export const transformAccessAndRolesServerData = (data: AccessLevelDTO[]): AccessData[] =>
    data.map((al: AccessLevelDTO) => ({
        id: al.id,
        name: al.name,
        deleted: al.deleted,
        permissions: al.permissions.map((p: PermissionDTO) => ({
            id: p.id,
            name: p.name,
            accessId: p.accessLevelId,
        })),
    }))

export const transformVersions = (
    serverData: {
        id: string
        version: number
    }[]
): ObjectOfVersions => _.mapValues(_.keyBy(serverData, "id"), "version")

export const isLoyaltyDataFetched = (data: Maybe<LoyaltyProgramData>) => data || data === undefined
